import React, { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Box } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-pro';
import CreateEditCategoryCard from './components/CreateEditCategoryCard';
import MobileRowMainContent from './components/MobileRowStructure';
import BuildColumns from './helpers/Columns';
import useFiltersFetchedData from '../../../helperHooks/useFiltersFetchedData';
import { Category, CategoryErrors, CategoryRaw } from '../../../types';
import { CATEGORIES_API_CACHE_KEY } from '../../../constants';
import PageHeader from '../../../components/pageHeaderComponents/PageHeader';
import GenericDataGridAndSideForm from '../../../components/datagridComponents/GenericDataGridAndSideForm';
import ActionsContainer from '../../../components/ActionsContainer';
import { FilterContextProvider } from '../../../context/FilterContext';
import { API_V1_HOSTNAME_KEY } from '../../../localStorageKeys';
import usePageTitle from '../../../helperHooks/usePageTitle';

const CategoriesContent = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  usePageTitle('nav.products');

  const {
    fetchData: refetch,
    data: categoryRawData,
    isLoading,
    isRefetching,
    isError,
    error,
  } = useFiltersFetchedData<{
    Data: CategoryRaw[];
    Metadata: {
      Page: number;
      Limit: number;
      TotalPages: number;
      TotalRecords: number;
      CurrentPageSize: number;
    };
  }>(
    CATEGORIES_API_CACHE_KEY,
    `${localStorage.getItem(API_V1_HOSTNAME_KEY)}onboarding/proxy/categories`,
    'categories'
  );

  const gridData: Category[] = useMemo(
    () =>
      categoryRawData && categoryRawData?.Data?.length > 0
        ? categoryRawData?.Data?.map((x) => ({
            ...x,
            id: x?.Id,
          }))
        : [],
    [categoryRawData]
  );

  const buildColumns = useCallback(
    (): GridColDef[] => BuildColumns({ t, categoriesData: gridData }),
    [gridData]
  );

  const unauthorized = (error as any)?.code === 401;

  return (
    <>
      <PageHeader omitTextHeader />
      <GenericDataGridAndSideForm<Category, CategoryErrors>
        pageDescription={t('screens.products.categories.description')}
        data={gridData}
        metadata={{
          page: categoryRawData?.Metadata?.Page,
          limit: categoryRawData?.Metadata?.Limit,
          totalPages: categoryRawData?.Metadata?.TotalPages,
          totalRecords: categoryRawData?.Metadata?.TotalRecords,
          currentPageSize: categoryRawData?.Metadata?.CurrentPageSize,
        }}
        isLoading={isLoading || isRefetching}
        isError={isError}
        unauthorized={unauthorized}
        fetchData={refetch}
        BuildColumns={buildColumns}
        CreateComponent={CreateEditCategoryCard}
        translationKey="products.categories"
        endpoint={`${localStorage.getItem(API_V1_HOSTNAME_KEY)}onboarding/proxy/categories`}
        sentryMsg="categories"
        cacheKey={CATEGORIES_API_CACHE_KEY}
        mobileRowMainContent={MobileRowMainContent}
      />
      <ActionsContainer>
        <Button
          variant="outlined"
          sx={{
            margin: '0px',
            flexGrow: '0',
            height: '48px',
          }}
          onClick={() => {
            navigate('/products');
          }}
        >
          {t('actions.previous')}
        </Button>
        <Button
          variant="contained"
          sx={{
            margin: '0px',
            flexGrow: '0',
            height: '48px',
          }}
          disabled={gridData?.length < 1}
          onClick={() => {
            navigate('/products-list');
          }}
        >
          {t('actions.next')}
        </Button>
      </ActionsContainer>
    </>
  );
};

const Categories = () => (
  <FilterContextProvider>
    <CategoriesContent />
  </FilterContextProvider>
);

export default Categories;
